import React, { useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { CategoryNameLocation, SiteCodes } from 'shared-types'
import { Button } from '~/components'
import { Image } from '~/components/Image'
import ImageOverlayLinks from '~/components/ImageOverlayLinks/ImageOverlayLinks'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrcSet } from '~/helpers'
import { useBraze, usePage } from '~/hooks'
import { BrazeBannerCard } from '~/sections/BrazeBannerCard'
import { ProductListingHeadProps } from '~/sections/ProductListing/ProductListing.types'
import styles from './ProductListing.module.scss'

const ProductListingHead = ({
  backgroundImage,
  categoryName,
  categoryNameAlignment,
  categoryNameColor,
  paragraph,
  hasSearchQuery = false,
  hideCategoryName = false,
  categoryNameLocation = CategoryNameLocation.InsideBanner,
  brazeBannerCards,
  onViewMore,
}: ProductListingHeadProps) => {
  const { siteCode } = usePage()
  const isRtm = siteCode === SiteCodes.RTM
  const desktopBannerImage = backgroundImage?.desktop || backgroundImage?.mobile
  const mobileBannerImage = backgroundImage?.mobile || backgroundImage?.desktop
  const showBannerWithTitle =
    desktopBannerImage?.src &&
    categoryNameLocation === CategoryNameLocation.InsideBanner

  const bannerStyle = useMemo(() => {
    const defaultMobileBannerHeight = '120px'
    const defaultDesktopBannerHeight = isRtm ? '220px' : '100%'

    const mobileBannerWidth =
      mobileBannerImage?.width != null
        ? `${mobileBannerImage?.width}px`
        : '100%'
    const mobileBannerHeight =
      mobileBannerImage?.height != null
        ? `${mobileBannerImage?.height}px`
        : defaultMobileBannerHeight
    const desktopBannerWidth =
      desktopBannerImage?.width != null
        ? `${desktopBannerImage?.width}px`
        : '100%'
    const desktopBannerHeight =
      desktopBannerImage?.height != null
        ? `${desktopBannerImage?.height}px`
        : defaultDesktopBannerHeight

    return {
      '--mobile-banner-width': mobileBannerWidth,
      '--mobile-banner-height': mobileBannerHeight,
      '--desktop-banner-width': desktopBannerWidth,
      '--desktop-banner-height': desktopBannerHeight,
    } as React.CSSProperties
  }, [desktopBannerImage, mobileBannerImage, isRtm])

  const renderCategoryName = () => {
    if (hideCategoryName || hasSearchQuery) {
      return null
    }

    const categoryNameStyle = categoryNameColor
      ? ({
          '--color-typography-heading': categoryNameColor,
        } as React.CSSProperties)
      : null

    return (
      <Typography
        style={categoryNameStyle}
        variant={TypographyVariant.Heading3}
        tag={TypographyTag.h1}
        className={classNames(
          {
            'flex justify-center': categoryNameAlignment === 'center',
            'font-bold font-display-large-desktop-font-family': !isRtm,
          },
          'text-2xl leading-display-large-desktop',
          'lg:text-display-x-large-desktop lg:leading-9'
        )}>
        {categoryName || ''}
      </Typography>
    )
  }

  return (
    <>
      {desktopBannerImage?.src && (
        <div
          className={classNames(
            'relative mt-2 mb-6 max-w-full',
            styles.bannerImage
          )}
          style={bannerStyle}>
          {showBannerWithTitle && (
            <div className='absolute inset-0 z-10 flex items-center p-4'>
              {renderCategoryName()}
            </div>
          )}
          <ImageOverlayLinks
            desktopImage={desktopBannerImage}
            mobileImage={mobileBannerImage}
          />
          <Image
            className={classNames(
              'object-cover rounded lg:!rounded-lg w-full h-full'
            )}
            src={desktopBannerImage.src}
            addSrcSet={false}
            alt={desktopBannerImage.altText}
            width={desktopBannerImage.width}
            height={desktopBannerImage.height}
            sizes='1280px'
            sources={
              <>
                <source
                  srcSet={getImageSrcSet(backgroundImage.mobile.src, [
                    {
                      intrinsicImageSize: '800',
                      width: '800',
                    },
                  ])}
                  media='(max-width: 768px)'
                />
                <source
                  srcSet={getImageSrcSet(backgroundImage?.tablet?.src, [
                    {
                      intrinsicImageSize: '1100',
                    },
                  ])}
                  media='(max-width: 1024px)'
                />
              </>
            }
          />
        </div>
      )}
      {!showBannerWithTitle && renderCategoryName()}
      {paragraph && (
        <div className='mt-2 flex gap-4 flex-col items-start'>
          <Typography
            tag={TypographyTag.p}
            className='flex justify-center'
            variant={TypographyVariant.BodyRegularExtraLineHeight}>
            {paragraph}
          </Typography>
          <Button
            status='primary'
            variant='ghost'
            theme={{ root: 'p-0 h-auto' }}
            style={{ '--btn-ghost-text-decoration': 'underline' }}
            onClick={onViewMore}>
            Learn More
          </Button>
        </div>
      )}
      {brazeBannerCards?.map((card) => {
        return <BrazeBannerCard key={card.bannerPlacementId} {...card} wrapperClassName='!p-0'/>
      })}
    </>
  )
}

export { ProductListingHead }
